/*
 * ---------------------------------------------------------------------------
 * This dashboard holds no credential, and that is the one way it differs
 * ---------------------------------------------------------------------------
 * `maxlayer-clients` and `maxlayer-admin` keep an access token in a module-level
 * variable and refresh it through an httpOnly cookie, with the one-refresh-
 * one-replay dance that CONVENTIONS.md describes. None of that is here, and its
 * absence is the design rather than a simplification.
 *
 * The session is an httpOnly cookie set by `maxlayer-content-api` — the same
 * shape the community frontend uses, and for the same reason. Every request
 * below is `credentials: "include"` and carries nothing else; there is no token
 * in memory to leak, nothing in `localStorage`, and no refresh to race. A 401
 * means the cookie is gone or revoked, and the answer is to sign in again
 * through the redirect flow rather than to renew anything.
 *
 * The cost is a cookie that must reach this origin, which is why
 * `VITE_API_URL` has to share a registrable domain with wherever this is
 * served. `.env.example` says so at length, because the failure is silent:
 * every page renders signed out with nothing anywhere to explain it.
 */

const configured = import.meta.env.VITE_API_URL?.replace(/\/+$/, "");

if (!configured) {
  throw new Error(
    "VITE_API_URL is required. Set it to the origin of the maxlayer-content-api Worker for this " +
      "environment — https://api.maxlayer.dev in production — without a path.",
  );
}

export const API_URL = configured;

/** The marketing site, for the one outbound link. Optional; the link is omitted without it. */
export const MARKETING_URL: string | null =
  import.meta.env.VITE_MARKETING_URL?.replace(/\/+$/, "") || null;

/**
 * The *platform* API, which is a different service from the one above.
 *
 * The only thing read from it is the list of promotion codes an affiliate may
 * hand out, and promotions live in the control plane because that is where
 * credit is granted — the affiliate programme's own database knows nothing
 * about them. The alternative was proxying the list through this programme's
 * API, which would have meant one service holding a second service's public
 * catalogue for no reason but to avoid a second origin.
 *
 * Read with `credentials: "omit"`. The endpoint is unauthenticated and answers
 * `Access-Control-Allow-Origin: *`, so a browser would refuse to attach the
 * session cookie anyway — and this dashboard's cookie belongs to a different
 * origin entirely. Optional: without it the offers card is not rendered, which
 * is the correct state for a deployment running no promotions.
 */
export const PLATFORM_API_URL: string | null =
  import.meta.env.VITE_PLATFORM_API_URL?.replace(/\/+$/, "") || null;

/**
 * The error envelope every Maxlayer service answers with, as an exception.
 *
 * `code` is what a caller branches on and `message` is what a person reads —
 * the contract `docs/CONVENTIONS.md` states for all seven repositories. Kept
 * identical here so a component written against one dashboard reads the same in
 * the other.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string | undefined;

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

/** Raised when the session is gone, so one place decides what that means. */
export class NotSignedIn extends ApiError {
  constructor(message = "Sign in to continue") {
    super(401, "unauthorized", message);
    this.name = "NotSignedIn";
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal } = options;

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      // The whole authentication mechanism. Without it every request is
      // anonymous and every page renders signed out — which is exactly what a
      // misconfigured origin looks like, hence the note at the top of this file.
      credentials: "include",
      ...(body === undefined
        ? {}
        : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    // An abort is the caller changing their mind — a query cancelled by a
    // navigation — and must not be reported as the service being unreachable.
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "network_error", "Could not reach Maxlayer. Check your connection.");
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    | { error?: { code?: string; message?: string; requestId?: string } }
    | null;

  if (!response.ok) {
    const error = payload?.error;
    if (response.status === 401) throw new NotSignedIn(error?.message);
    throw new ApiError(
      response.status,
      error?.code ?? "unknown",
      error?.message ?? "Something went wrong.",
      error?.requestId,
    );
  }

  return payload as T;
}

/**
 * Where to send somebody who has no session.
 *
 * A top-level navigation to the content API, which redirects on to the
 * dashboard's consent screen and eventually back here. Every step of that is a
 * navigation rather than a fetch, which is the property the whole flow was
 * built for — a browser blocking third-party cookies is not a dead end.
 *
 * `return_to` is a path on this site and is checked as one at the far end, so a
 * crafted value cannot bounce somebody elsewhere. Sent anyway rather than
 * assumed, because landing back on the page you were trying to open is the
 * difference between signing in and starting again.
 */
export function signInUrl(returnTo: string): string {
  const target = new URL("/v1/affiliate/auth/oauth/start", API_URL);
  target.searchParams.set("return_to", returnTo);
  return target.href;
}
